<template>
  <v-container fluid class="pa-4">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
          <div class="d-flex align-center">
            <v-avatar color="info" size="44" class="mr-3">
              <v-icon color="white">mdi-account-switch</v-icon>
            </v-avatar>
            <div>
              <div class="text-h5 font-weight-bold">{{ $t('role.user_role.manager.title') }}</div>
              <div class="text-body-2 text--secondary">{{ $t('role.user_role.manager.subtitle') }}</div>
            </div>
          </div>
          <v-btn text small :to="{ name: 'roles' }">
            <v-icon left small>mdi-arrow-left</v-icon>
            {{ $t('role.button.all') }}
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Users Table -->
    <v-card elevation="1">
      <v-card-title class="py-3 px-4">
        <v-row dense align="center">
          <v-col cols="12" sm="5">
            <v-text-field
              v-model="search"
              :placeholder="$t('text.search_placeholder')"
              prepend-inner-icon="mdi-magnify"
              clearable dense outlined hide-details
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-select
              v-model="roleFilter"
              :items="roleFilterItems"
              :label="$t('role.label.filter_by_role')"
              clearable dense outlined hide-details
            />
          </v-col>
        </v-row>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="filteredUsers"
        :loading="loading"
        :items-per-page="15"
        :footer-props="{ 'items-per-page-options': [10, 15, 25, 50] }"
      >
        <template slot="item.name" slot-scope="{ item }">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="32" class="mr-2">
              <span class="white--text text-caption font-weight-bold">
                {{ item.name.charAt(0).toUpperCase() }}
              </span>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ item.name }}</div>
              <div class="text-caption text--secondary">{{ item.email }}</div>
            </div>
          </div>
        </template>

        <template slot="item.roles" slot-scope="{ item }">
          <div class="d-flex flex-wrap py-1" style="gap: 4px">
            <v-chip
              v-for="role in item.roles"
              :key="role.id"
              x-small
              :color="roleColor(role.name)"
              dark
            >
              {{ role.name }}
            </v-chip>
            <span v-if="!item.roles || item.roles.length === 0" class="text-caption text--disabled">
              {{ $t('role.user_role.no_roles') }}
            </span>
          </div>
        </template>

        <template slot="item.active" slot-scope="{ item }">
          <v-chip x-small :color="item.active ? 'success' : 'grey'" dark>
            {{ item.active ? $t('label.active') : $t('label.inactive') }}
          </v-chip>
        </template>

        <template slot="item.actions" slot-scope="{ item }">
          <v-btn small outlined color="primary" @click="openManage(item)">
            <v-icon left small>mdi-account-cog</v-icon>
            {{ $t('role.user_role.button.manage') }}
          </v-btn>
        </template>

        <template v-slot:no-data>
          <div class="text-center py-8">
            <v-icon color="grey lighten-1" size="56">mdi-account-off</v-icon>
            <p class="text--secondary mt-2">{{ $t('user.label.no_users') }}</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Manage Roles Dialog -->
    <v-dialog v-model="manageDialog" max-width="480" persistent>
      <v-card v-if="selectedUser">
        <v-card-title class="pa-4">
          <v-avatar color="primary" size="32" class="mr-2">
            <span class="white--text text-caption font-weight-bold">
              {{ selectedUser.name.charAt(0).toUpperCase() }}
            </span>
          </v-avatar>
          {{ selectedUser.name }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-3">
          <div class="text-subtitle-2 mb-3">{{ $t('role.user_role.assign_roles') }}</div>
          <v-row dense>
            <v-col cols="6" v-for="role in allRoles" :key="role.id">
              <v-checkbox
                v-model="selectedRoles"
                :value="role.id"
                :label="role.name"
                dense hide-details class="mt-0"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <span class="text-caption text--secondary">
            {{ selectedRoles.length }} {{ $t('role.label.selected') }}
          </span>
          <v-spacer />
          <v-btn text @click="closeManage">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveRoles" :loading="saving">{{ $t('button.save_changes') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="3000">
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  name: 'UserRoleManager',
  data () {
    return {
      users: [],
      allRoles: [],
      loading: false,
      search: '',
      roleFilter: null,
      manageDialog: false,
      selectedUser: null,
      selectedRoles: [],
      saving: false,
      snackbar: { show: false, text: '', color: 'success' }
    }
  },
  computed: {
    headers () {
      return [
        { text: this.$t('user.label.name'), value: 'name', width: '240px' },
        { text: this.$t('role.title.role'), value: 'roles', sortable: false },
        { text: this.$t('label.status'), value: 'active', width: '100px' },
        { text: this.$t('label.actions'), value: 'actions', sortable: false, width: '140px', align: 'center' }
      ]
    },
    roleFilterItems () {
      return this.allRoles.map(r => ({ text: r.name, value: r.id }))
    },
    filteredUsers () {
      let result = this.users
      if (this.search) {
        const q = this.search.toLowerCase()
        result = result.filter(u =>
          u.name.toLowerCase().includes(q) ||
          (u.email && u.email.toLowerCase().includes(q))
        )
      }
      if (this.roleFilter) {
        result = result.filter(u =>
          u.roles && u.roles.some(r => r.id === this.roleFilter)
        )
      }
      return result
    }
  },
  methods: {
    roleColor (name) {
      const map = {
        administrator: 'error',
        manager: 'warning',
        cashier: 'success',
        service: 'info',
        employee: 'grey darken-1'
      }
      return map[name.toLowerCase()] || 'primary'
    },
    openManage (user) {
      this.selectedUser = user
      this.selectedRoles = (user.roles || []).map(r => r.id)
      this.manageDialog = true
    },
    closeManage () {
      this.manageDialog = false
      this.selectedUser = null
      this.selectedRoles = []
    },
    async saveRoles () {
      this.saving = true
      try {
        await this.$roleService.assignUserRoles(this.selectedUser.id, { roles: this.selectedRoles })
        this.notify(this.$t('role.user_role.message.updated'))
        await this.loadData()
        this.closeManage()
      } catch (e) {
        console.error(e)
      } finally {
        this.saving = false
      }
    },
    notify (text, color = 'success') {
      this.snackbar = { show: true, text, color }
    },
    async loadData () {
      this.loading = true
      try {
        const [users, roles] = await Promise.all([
          this.$userService.getUsers(),
          this.$roleService.getRoles()
        ])
        this.users = users.map(u => ({ ...u, roles: u.roles || [] }))
        this.allRoles = roles
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    }
  },
  mounted () {
    this.$emit('parent-title', this.$t('role.user_role.manager.title'))
    this.loadData()
  }
}
</script>
